import * as ToastPrimitive from '@radix-ui/react-toast';
import { create } from 'zustand';
import { useEffect, type ReactNode } from 'react';
import { CheckCircle2, XCircle, Info, X } from 'lucide-react';
import { cn } from '@/lib/cn';

type Tone = 'success' | 'error' | 'info';
interface ToastEntry {
  id: number;
  tone: Tone;
  title: string;
  description?: string;
}

interface ToastState {
  items: ToastEntry[];
  add: (entry: Omit<ToastEntry, 'id'>) => void;
  remove: (id: number) => void;
}

let seq = 1;

const useToastStore = create<ToastState>((set) => ({
  items: [],
  add: (entry) =>
    set((state) => ({ items: [...state.items, { ...entry, id: seq++ }] })),
  remove: (id) =>
    set((state) => ({ items: state.items.filter((i) => i.id !== id) })),
}));

export function toast(entry: Omit<ToastEntry, 'id'>) {
  useToastStore.getState().add(entry);
}

const tones: Record<Tone, { icon: ReactNode; ring: string }> = {
  success: {
    icon: <CheckCircle2 className="h-5 w-5 text-[var(--color-success)]" />,
    ring: 'ring-[var(--color-success)]',
  },
  error: {
    icon: <XCircle className="h-5 w-5 text-[var(--color-error)]" />,
    ring: 'ring-[var(--color-error)]',
  },
  info: {
    icon: <Info className="h-5 w-5 text-[var(--color-info)]" />,
    ring: 'ring-[var(--color-info)]',
  },
};

export function Toaster() {
  const items = useToastStore((s) => s.items);
  const remove = useToastStore((s) => s.remove);

  useEffect(() => {
    if (items.length === 0) return;
    const timers = items.map((item) =>
      setTimeout(() => remove(item.id), 4000),
    );
    return () => timers.forEach(clearTimeout);
  }, [items, remove]);

  return (
    <ToastPrimitive.Provider swipeDirection="right">
      {items.map((item) => (
        <ToastPrimitive.Root
          key={item.id}
          className={cn(
            'glass-elevated flex w-full max-w-sm items-start gap-3 p-4 shadow-[var(--shadow-elevated)] ring-1 ring-inset',
            tones[item.tone].ring,
          )}
          onOpenChange={(open) => !open && remove(item.id)}
        >
          {tones[item.tone].icon}
          <div className="flex-1">
            <ToastPrimitive.Title className="text-sm font-semibold text-[var(--color-fg-primary)]">
              {item.title}
            </ToastPrimitive.Title>
            {item.description && (
              <ToastPrimitive.Description className="mt-0.5 text-xs text-[var(--color-fg-muted)]">
                {item.description}
              </ToastPrimitive.Description>
            )}
          </div>
          <ToastPrimitive.Close className="text-[var(--color-fg-subtle)] hover:text-[var(--color-fg-primary)]">
            <X className="h-4 w-4" />
          </ToastPrimitive.Close>
        </ToastPrimitive.Root>
      ))}
      <ToastPrimitive.Viewport className="fixed inset-x-0 bottom-0 z-[60] flex w-full flex-col items-center gap-3 p-4 outline-none sm:inset-x-auto sm:bottom-6 sm:right-6 sm:max-w-sm sm:items-stretch sm:p-0" />
    </ToastPrimitive.Provider>
  );
}
